import { FormattedConnector } from 'src/hooks/useConnectorAccount';
import {
  AnalyticsAccount,
  Connector,
  ConnectorType,
  FacebookAdAccount,
  GoogleAdsAccount,
  DatabaseConnection,
  SearchConsoleSite,
} from 'src/utils/gql/types';

type AccountsData =
  | { [key: string]: AnalyticsAccount[] }
  | { [key: string]: GoogleAdsAccount[] }
  | { [key: string]: FacebookAdAccount[] }
  | { [key: string]: SearchConsoleSite[] }
  | { [key: string]: DatabaseConnection[] };

const mapAnalyticsAccount = (acc: AnalyticsAccount) => ({
  id: acc.id,
  name: `${acc.name} - ${acc.id}`,
  selected: acc.selected,
  properties: acc.properties.map((prop) => ({
    id: prop.id,
    name: prop.name,
    selected: prop.selected,
  })),
});

const mapGoogleAdsAccount = (acc: GoogleAdsAccount) => ({
  id: acc.id,
  name: acc.id,
  selected: acc.selected,
  properties: acc.subaccounts.map((subAcc) => ({
    id: subAcc.id,
    name: `${subAcc.name} - ${subAcc.id}`,
    selected: subAcc.selected,
  })),
});

const mapFacebookAdAccount = (acc: FacebookAdAccount) => ({
  id: acc.id,
  name: `${acc.name} - ${acc.id.replace('act_', '')}`,
  selected: acc.selected,
  properties: [],
});

const mapSearchConsoleSite = (acc: SearchConsoleSite) => ({
  id: acc.id,
  name: acc.name,
  selected: acc.selected,
  properties: [],
});

const mapDatabaseConnection = (acc: DatabaseConnection) => ({
  id: acc.id,
  name: acc.name,
  selected: acc.selected,
  properties:
    (acc as any).properties?.map(
      (prop: { id: string; name: string; selected: boolean }) => ({
        id: prop.id,
        name: prop.name,
        selected: prop.selected,
      }),
    ) || [],
});

export const updateConnectorsWithAccounts = (
  connectors: Connector[],
  accounts: AccountsData,
  connectorType: ConnectorType,
): FormattedConnector[] => {
  return connectors.map((connector, index) => {
    const accountKey = `getAccounts${index}`;
    const accountData = accounts[accountKey];

    let connectorAccounts: FormattedConnector['accounts'] = [];

    switch (connectorType) {
      case ConnectorType.GOOGlE_ANALYTICS4:
        connectorAccounts = ((accountData as AnalyticsAccount[]) || []).map(
          mapAnalyticsAccount,
        );
        break;

      case ConnectorType.GOOGLE_ADS:
        connectorAccounts = ((accountData as GoogleAdsAccount[]) || []).map(
          mapGoogleAdsAccount,
        );
        break;

      case ConnectorType.FACEBOOK:
        connectorAccounts = ((accountData as FacebookAdAccount[]) || []).map(
          mapFacebookAdAccount,
        );
        break;

      case ConnectorType.GOOGLE_SEARCH_CONSOLE:
        connectorAccounts = ((accountData as SearchConsoleSite[]) || []).map(
          mapSearchConsoleSite,
        );
        break;

      case ConnectorType.DATABASE:
        connectorAccounts = ((accountData as DatabaseConnection[]) || []).map(
          mapDatabaseConnection,
        );
        break;

      default:
        break;
    }

    return {
      ...connector,
      accounts: connectorAccounts,
    };
  });
};
